import { LayoutEngine } from '@src/editor/models/layout/LayoutEngine';
import { useReframeOnFaceMapChange } from '@src/hooks/useReframeOnFaceMapChange';
import { useSpeakerFaceMapping } from '@src/hooks/useSpeakerFaceMapping';
import { useStorySpeakers } from '@src/hooks/useStorySpeakers';
import { SmartFramingService } from '@src/services/framing/SmartFramingService';
import {
  createScenesAndAutoframeZoomCallIn9by16,
  createScenesPerSpeaker,
} from '@src/services/framing/zoomCall';
import { SceneService } from '@src/services/SceneService';
import { getSpeakerColor } from '@src/services/SpeakerManagementService';
import { useEditor } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CameraIcon from '@src/svgs/CameraIcon';
import CropIcon from '@src/svgs/CropIcon';
import FrameLockIcon from '@src/svgs/FrameLockIcon';
import GridIcon from '@src/svgs/GridIcon';
import ScaleIcon from '@src/svgs/ScaleIcon';
import SwitchUserIcon from '@src/svgs/SwitchUserIcon';
import { AspectRatio } from '@src/types.ts/video';
import { LogManager } from '@src/utility/Logger';
import parseAspectRatio from '@src/utility/parseAspectRatio';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import FaceMappingModal from './modals/FaceMappingModal';

import type { Story } from '../../types.ts/story';

const logger = LogManager.getLogger('feature:AIFramingProducer');

type Props = {
  story?: Story;
};

const AIFramingProducer = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const editor = useEditor().editor;
  const [framingService, setFramingService] =
    useState<SmartFramingService | null>(null);

  const storyId = props.story?.id || videoCreator.story?.id;
  const {
    faceDetections,
    speakerFaceMapping,
    speakerIdentifications,
    faceTracks,
    loading,
    detecting,
    identifying,
    savingOverride,
    runIdentifySpeakers,
    saveSpeakerIdentificationsOverride,
    voiceMappings,
    speakerLabelToFaceLabels,
  } = useSpeakerFaceMapping(storyId, true); // Enable polling every 5 seconds

  const { speakers, hasSpeakerWithHeadshot } = useStorySpeakers(storyId);

  const showcaseId =
    props.story?.primaryShowcase?.id || videoCreator.story?.primaryShowcase?.id;

  const [mappingModalOpen, setMappingModalOpen] = useState(false);

  useEffect(() => {
    if (editor) {
      const sceneService = new SceneService();
      const layoutEngine = new LayoutEngine();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const service = new SmartFramingService(
        editor as any,
        sceneService,
        layoutEngine,
      );
      setFramingService(service);
    }
  }, [editor]);

  // Resolve the scene under the playhead, or null if none.
  const getCurrentSceneInfo = () => {
    const scene = editor?.timeline.getCurrentScene(videoCreator.time);
    if (!scene) console.warn('No scene found at current time');
    return scene ?? null;
  };

  // Group A — Active Speaker (Face-Speaker overlap): uses speakerFaceMapping.
  const handleIntervalsCurrentScene = () => {
    if (
      !faceDetections ||
      faceDetections.length === 0 ||
      !framingService ||
      Object.keys(speakerFaceMapping).length === 0
    ) {
      return;
    }
    const sceneInfo = getCurrentSceneInfo();
    if (!sceneInfo) return;
    framingService.autoFrameSingleSceneWithIntervalsCommand(
      sceneInfo,
      faceDetections,
      speakerFaceMapping,
      getTargetAspectRatio(),
    );
  };

  const handleIntervalsAllScenes = () => {
    if (
      !faceDetections ||
      faceDetections.length === 0 ||
      !framingService ||
      Object.keys(speakerFaceMapping).length === 0
    ) {
      return;
    }
    framingService.autoFrameAllScenesWithIntervals(
      faceDetections,
      speakerFaceMapping,
      getTargetAspectRatio(),
    );
  };

  // Group B — Active Speaker (Speaker-Face matching): uses speakerLabelToFaceLabels.
  const handleSpeakerContextCurrentScene = () => {
    if (
      !faceDetections ||
      faceDetections.length === 0 ||
      !framingService ||
      Object.keys(speakerLabelToFaceLabels).length === 0
    ) {
      return;
    }
    const sceneInfo = getCurrentSceneInfo();
    if (!sceneInfo) return;
    framingService.autoFrameSingleSceneWithSpeakerContextCommand(
      sceneInfo,
      faceDetections,
      speakerLabelToFaceLabels,
      getTargetAspectRatio(),
    );
  };

  const handleSpeakerContextAllScenes = () => {
    if (
      !faceDetections ||
      faceDetections.length === 0 ||
      !framingService ||
      Object.keys(speakerLabelToFaceLabels).length === 0
    ) {
      return;
    }
    framingService.autoFrameAllScenesWithSpeakerContext(
      faceDetections,
      speakerLabelToFaceLabels,
      getTargetAspectRatio(),
    );
  };

  // Group C — Basic: face detections only.
  const handleBasicCurrentScene = () => {
    if (!faceDetections || faceDetections.length === 0 || !framingService) {
      return;
    }
    const sceneInfo = getCurrentSceneInfo();
    if (!sceneInfo) return;
    framingService.autoFrameSingleSceneCommand(
      sceneInfo,
      faceDetections,
      getTargetAspectRatio(),
    );
  };

  const handleBasicAllScenes = () => {
    if (!faceDetections || faceDetections.length === 0 || !framingService) {
      return;
    }
    framingService.autoFrameAllScenes(faceDetections, getTargetAspectRatio());
  };

  const getTargetAspectRatio = (): number | undefined => {
    const currentAspectRatio = videoCreator.getCurrentAspectRatio();

    // Smart framing defaults to 9:16 when no target is provided; pass through
    // the numeric ratio for any other aspect so crops size correctly (e.g. 1:1).
    if (currentAspectRatio !== AspectRatio.AR_9_16) {
      return parseAspectRatio(currentAspectRatio) ?? undefined;
    }

    return undefined;
  };

  const handleAddFaceTracks = () => {
    if (faceDetections && framingService) {
      framingService.addFaceDetectionTracks(faceDetections);
    }
  };

  // Group A — Active Speaker (Face-Speaker Overlap): colours each track via
  // speaker_label resolved from `speakerFaceMapping` (face_label → speaker_label).
  const handleAddActiveSpeakerFaceTracksColoredBySpeaker = () => {
    if (
      !faceDetections ||
      faceDetections.length === 0 ||
      !framingService ||
      Object.keys(speakerFaceMapping).length === 0
    ) {
      return;
    }
    framingService.addActiveSpeakerFaceDetectionTracksColoredBySpeaker(
      faceDetections,
      speakerFaceMapping,
      undefined,
      { clearOtherTracks: true },
    );
  };

  // -------------------------------------------------------------------------
  // Experimental — Zoom Call
  // -------------------------------------------------------------------------

  const handleCreateScenesPerSpeaker = () => {
    if (!editor) return;
    createScenesPerSpeaker({ editor, shots: videoCreator.sceneDetections });
  };

  const handleFrameAsZoomCall = () => {
    if (
      !editor ||
      !faceDetections ||
      faceDetections.length === 0 ||
      Object.keys(speakerLabelToFaceLabels).length === 0
    ) {
      return;
    }
    createScenesAndAutoframeZoomCallIn9by16({
      editor,
      faceDetections,
      speakerLabelToFaceLabels,
      naturalMediaDimensions: videoCreator.preloadedNaturalDimensions,
    });
  };

  const handleAutoLayoutCurrentScene = useCallback(() => {
    if (!framingService || !faceDetections) {
      console.warn('Service not initialized or no face detections available');
      return;
    }

    const currentTime = videoCreator.time;
    const currentScene = editor?.timeline.getCurrentScene(currentTime);
    if (!currentScene) return;

    const update = framingService.autoLayoutScene(
      currentScene.scene,
      faceDetections,
    );
    if (update) {
      editor.commandUpdateScene(update.sceneId, update.updates);
    }
  }, [editor, faceDetections, framingService]);

  const handleAutoLayoutAllScenes = useCallback(() => {
    console.log('Auto-layout all scenes invoked');
    if (!framingService || !faceDetections || faceDetections.length === 0) {
      console.error('No face detections available or service not initialized');
      return false;
    }

    const scenes = editor.timeline.scenes;
    if (!scenes || scenes.length === 0) {
      console.warn('No scenes to auto-layout');
      return false;
    }

    const updates = framingService.autoLayoutAllScenes(faceDetections);

    if (updates.length === 0) {
      console.log('No scenes needed layout updates');
      return false;
    }

    const success = editor.commandUpdateScenes(updates);

    if (success) {
      console.log(`Auto-layouted ${updates.length} scene(s)`);
    } else {
      console.warn('Failed to auto-layout scenes');
    }

    return success;
  }, [editor, faceDetections, framingService]);

  // Group B — Active Speaker (Speaker-Face Matching): colours each track via
  // the Dato speakerId resolved from `speakerIdentifications` (face_label →
  // speakerId), falling back to the transcript speaker_label.
  const handleAddFaceTracksColoredBySpeaker = () => {
    if (
      !faceDetections ||
      faceDetections.length === 0 ||
      !framingService ||
      Object.keys(speakerLabelToFaceLabels).length === 0
    ) {
      return;
    }
    const faceLabelToColor: Record<string, string> = {};
    for (const [speakerLabel, faceLabels] of Object.entries(
      speakerLabelToFaceLabels,
    )) {
      for (const faceLabel of faceLabels) {
        const speakerId = speakerIdentifications[faceLabel];
        faceLabelToColor[faceLabel] = getSpeakerColor(
          speakerId ?? speakerLabel,
          0,
        );
      }
    }
    framingService.addFaceDetectionTracksColoredBySpeaker(
      faceDetections,
      faceLabelToColor,
      undefined,
      { clearOtherTracks: true },
    );
  };

  const reframeOnFaceMapChange = useReframeOnFaceMapChange({
    editor,
    storyId,
    faceDetections,
    speakerLabelToFaceLabels,
    detecting,
  });

  const runIdentification = useCallback(
    async (forceRerun: boolean) => {
      if (!storyId) return;
      if (!hasSpeakerWithHeadshot) {
        logger.warn(
          'Cannot run identification: no speaker headshots available',
        );
        return;
      }
      reframeOnFaceMapChange.expectChange(forceRerun);
      const result = await runIdentifySpeakers({ showcaseId, forceRerun });
      if (!result) reframeOnFaceMapChange.cancel();
    },
    [
      storyId,
      showcaseId,
      hasSpeakerWithHeadshot,
      runIdentifySpeakers,
      reframeOnFaceMapChange,
    ],
  );

  const saveOverrideAndReframe = useCallback(
    async (map: Record<string, string>): Promise<boolean> => {
      reframeOnFaceMapChange.expectChange(false);
      const saved = await saveSpeakerIdentificationsOverride(map);
      if (!saved) reframeOnFaceMapChange.cancel();
      return saved;
    },
    [saveSpeakerIdentificationsOverride, reframeOnFaceMapChange],
  );

  const handleConfirmedFullRerun = useCallback(() => {
    if (
      !window.confirm(
        'Re-run the full detection pipeline? This is expensive and will discard manual remaps.',
      )
    ) {
      return;
    }
    void runIdentification(true);
  }, [runIdentification]);

  const videoUrl =
    videoCreator.story?.originalVideo?.video?.mp4UrlLow ||
    videoCreator.story?.originalVideo?.video?.mp4Url ||
    videoCreator.story?.originalVideo?.video?.mp4UrlMedium ||
    videoCreator.story?.originalVideo?.video?.mp4UrlHigh;

  const runDisabled =
    !faceDetections ||
    !hasSpeakerWithHeadshot ||
    identifying ||
    detecting ||
    !storyId;

  const handleShowCropsFor916 = () => {
    if (!faceDetections || faceDetections.length === 0 || !framingService) {
      console.warn('No face detections available or service not initialized');
      return;
    }

    framingService.showCropBoxes916(faceDetections);
  };

  const getFaceCount = () => {
    if (!faceTracks) return 0;
    return Object.keys(faceTracks).length;
  };

  const getFrameCount = () => {
    if (!faceDetections) return 0;
    return faceDetections.length;
  };

  return (
    <Main>
      <ResultsSection>
        {loading && <LoadingText>Loading face detections...</LoadingText>}

        {detecting && <LoadingText>Detecting faces...</LoadingText>}

        {!loading && !detecting && faceDetections === null && (
          <EmptyState>
            No face detections yet. Click "Find Faces" to start.
          </EmptyState>
        )}

        {!loading &&
          !detecting &&
          faceDetections !== null &&
          faceDetections.length > 0 && (
            <Results>
              <ResultHeader>Face Detection Results</ResultHeader>
              <ResultStats>
                <StatItem>
                  <StatLabel>Total Faces:</StatLabel>
                  <StatValue>{getFaceCount()}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Frames with Faces:</StatLabel>
                  <StatValue>{getFrameCount()}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Active Speakers identified:</StatLabel>
                  <StatValue>
                    {Object.keys(speakerIdentifications).length}
                  </StatValue>
                </StatItem>
              </ResultStats>

              {(() => {
                const hasIntervalsMapping =
                  Object.keys(speakerFaceMapping).length > 0;
                const hasSpeakerContextMapping =
                  Object.keys(speakerLabelToFaceLabels).length > 0;
                const intervalsTitle = hasIntervalsMapping
                  ? ''
                  : 'No face↔speaker overlap mapping — needs transcript speakers overlapping face detections';
                const speakerContextTitle = hasSpeakerContextMapping
                  ? ''
                  : 'No speaker→face mapping — needs voice mappings + face identifications';
                return (
                  <>
                    <SectionHeader>
                      Active Speaker Framing (Face-Speaker Overlap)
                    </SectionHeader>
                    <ActionsSection>
                      <ActionButton
                        onClick={handleIntervalsCurrentScene}
                        disabled={!hasIntervalsMapping}
                        title={intervalsTitle}
                      >
                        <ScaleIcon
                          width="14"
                          height="14"
                          strokeColor="currentColor"
                        />
                        Auto-Frame Current Scene
                      </ActionButton>
                      <ActionButton
                        onClick={handleIntervalsAllScenes}
                        disabled={!hasIntervalsMapping}
                        title={intervalsTitle}
                      >
                        <CropIcon
                          width="14"
                          height="14"
                          fillColor="currentColor"
                        />
                        Auto-Frame All Scenes
                      </ActionButton>
                      <ActionButton
                        onClick={
                          handleAddActiveSpeakerFaceTracksColoredBySpeaker
                        }
                        disabled={!hasIntervalsMapping}
                        title={intervalsTitle}
                      >
                        <SwitchUserIcon
                          width="14"
                          height="14"
                          fillColor="currentColor"
                        />
                        Add Face Tracks (Colored by Speaker)
                      </ActionButton>
                    </ActionsSection>

                    <SectionDivider />

                    <SectionHeader>
                      Active Speaker Framing (Speaker-Face Matching)
                    </SectionHeader>
                    <ActionsSection>
                      <ActionButton
                        onClick={handleSpeakerContextCurrentScene}
                        disabled={!hasSpeakerContextMapping}
                        title={speakerContextTitle}
                      >
                        <ScaleIcon
                          width="14"
                          height="14"
                          strokeColor="currentColor"
                        />
                        Auto-Frame Current Scene
                      </ActionButton>
                      <ActionButton
                        onClick={handleSpeakerContextAllScenes}
                        disabled={!hasSpeakerContextMapping}
                        title={speakerContextTitle}
                      >
                        <CropIcon
                          width="14"
                          height="14"
                          fillColor="currentColor"
                        />
                        Auto-Frame All Scenes
                      </ActionButton>
                      <ActionButton
                        onClick={handleAddFaceTracksColoredBySpeaker}
                        disabled={!hasSpeakerContextMapping}
                        title={speakerContextTitle}
                      >
                        <SwitchUserIcon
                          width="14"
                          height="14"
                          fillColor="currentColor"
                        />
                        Add Face Tracks (Colored by Speaker)
                      </ActionButton>
                    </ActionsSection>

                    <SectionDivider />

                    <SectionHeader>Basic Framing</SectionHeader>
                    <ActionsSection>
                      <ActionButton onClick={handleBasicCurrentScene}>
                        <ScaleIcon
                          width="14"
                          height="14"
                          strokeColor="currentColor"
                        />
                        Auto-Frame Current Scene
                      </ActionButton>
                      <ActionButton onClick={handleBasicAllScenes}>
                        <CropIcon
                          width="14"
                          height="14"
                          fillColor="currentColor"
                        />
                        Auto-Frame All Scenes
                      </ActionButton>
                      <ActionButton onClick={handleAddFaceTracks}>
                        <SwitchUserIcon
                          width="14"
                          height="14"
                          fillColor="currentColor"
                        />
                        Add Face Tracks
                      </ActionButton>
                    </ActionsSection>

                    <SectionDivider />

                    <SectionHeader>Layout & Crop Debug</SectionHeader>
                    <ActionsSection>
                      <ActionButton onClick={handleAutoLayoutCurrentScene}>
                        <GridIcon
                          width="14"
                          height="14"
                          strokeColor="currentColor"
                        />
                        Auto-Layout Current Scene
                      </ActionButton>
                      <ActionButton onClick={handleAutoLayoutAllScenes}>
                        <FrameLockIcon
                          width="14"
                          height="14"
                          fillColor="currentColor"
                        />
                        Auto-Layout All Scenes
                      </ActionButton>
                      <ActionButton onClick={handleShowCropsFor916}>
                        <CameraIcon
                          strokeColor="currentColor"
                          width="14"
                          height="11"
                        />
                        Show Crops for 9:16
                      </ActionButton>
                    </ActionsSection>
                  </>
                );
              })()}

              <SectionDivider />

              {(() => {
                const hasSpeakerContextMapping =
                  Object.keys(speakerLabelToFaceLabels).length > 0;
                const zoomCallTitle = hasSpeakerContextMapping
                  ? ''
                  : 'No speaker→face mapping — needs voice mappings + face identifications';
                return (
                  <>
                    <SectionHeader>Experimental — Zoom Call</SectionHeader>
                    <ActionsSection>
                      <ActionButton
                        onClick={handleCreateScenesPerSpeaker}
                        disabled={!editor}
                      >
                        <GridIcon
                          width="14"
                          height="14"
                          strokeColor="currentColor"
                        />
                        Create Scenes per Speaker
                      </ActionButton>
                      <ActionButton
                        onClick={handleFrameAsZoomCall}
                        disabled={!hasSpeakerContextMapping}
                        title={zoomCallTitle}
                      >
                        <CropIcon
                          width="14"
                          height="14"
                          fillColor="currentColor"
                        />
                        Frame as Zoom Call
                      </ActionButton>
                    </ActionsSection>
                  </>
                );
              })()}

              <SectionDivider />

              <SectionHeader>Manage Faces</SectionHeader>
              <ActionsSection>
                <ActionButton
                  onClick={() => runIdentification(false)}
                  disabled={runDisabled}
                  title={
                    !hasSpeakerWithHeadshot
                      ? 'Add at least one headshot to a speaker first'
                      : ''
                  }
                >
                  {identifying && !detecting ? (
                    <>
                      <InlineSpinner aria-hidden />
                      Re-running…
                    </>
                  ) : (
                    'Re-run Identification'
                  )}
                </ActionButton>
                <ActionButton
                  onClick={handleConfirmedFullRerun}
                  disabled={runDisabled}
                  title={
                    !hasSpeakerWithHeadshot
                      ? 'Add at least one headshot to a speaker first'
                      : ''
                  }
                >
                  {detecting ? (
                    <>
                      <InlineSpinner aria-hidden />
                      Detecting…
                    </>
                  ) : (
                    'Force Re-run All Detections'
                  )}
                </ActionButton>
                {(identifying || detecting) && (
                  <RunStatus role="status">
                    {detecting
                      ? 'Running full detection — this can take a few minutes.'
                      : 'Running identification (≈10s)…'}
                  </RunStatus>
                )}
                <ActionButton onClick={() => setMappingModalOpen(true)}>
                  Open Face Mapping
                </ActionButton>
              </ActionsSection>
            </Results>
          )}
      </ResultsSection>

      {storyId && (
        <FaceMappingModal
          isOpen={mappingModalOpen}
          onClose={() => setMappingModalOpen(false)}
          storyId={storyId}
          videoUrl={videoUrl}
          faceDetections={faceDetections ?? []}
          faceTracks={faceTracks ?? {}}
          speakerIdentifications={speakerIdentifications ?? {}}
          speakerFaceMapping={speakerFaceMapping}
          voiceMappings={voiceMappings}
          speakers={speakers}
          hasSpeakerWithHeadshot={hasSpeakerWithHeadshot}
          identifying={identifying}
          detecting={detecting}
          savingOverride={savingOverride}
          onRunIdentification={runIdentification}
          onSaveOverride={saveOverrideAndReframe}
        />
      )}
    </Main>
  );
});

export default AIFramingProducer;

const Main = styled.div`
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LoadingText = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: center;
  padding: var(--spacing-md);
`;

const EmptyState = styled.div`
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: center;
  padding: var(--spacing-md);
`;

const Results = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ResultHeader = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const ResultStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: var(--surface);
  border-radius: 8px;
  border: 1px solid var(--border);
`;

const ActionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;

  &:hover {
    border-color: var(--foreground);
  }

  &:active {
    background: var(--surface);
  }
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StatLabel = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const StatValue = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 4px 0;
`;

const SectionHeader = styled.div`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const InlineSpinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  margin-right: 8px;
  animation: aifp-inline-spin 0.8s linear infinite;
  vertical-align: -2px;

  @keyframes aifp-inline-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const RunStatus = styled.div`
  font-size: var(--text-xs);
  color: var(--text-muted);
  padding: 4px 4px 0;
  font-style: italic;
`;
